import React, { useCallback, useEffect, useState } from "react";
import CollaboratorButton from "../collaboratorButton";
import { auth } from "../../services/firebase.js";
import CollaborationHistory from "./modals/collaborationHistory.tsx";
import {
  useDrillViewData,
  useDrillDocumentData,
  useUserData,
  useCollaborationData,
} from "../hooks/useFieldEditorShared.tsx";

export default function FieldEditorHeader() {
  // Get data from context hooks
  const { drill } = useDrillViewData();
  const { drillDocumentData, updateDrillDocumentData } = useDrillDocumentData();
  const userData = useUserData();
  const collaborationReturn = useCollaborationData();

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(drill?.title);

  const {
    drillModifiedByCollaboration,
    otherCollaborators,
    canUndo,
    canRedo,
    collaboration,
    collaborationHistoryLoading,
  } = collaborationReturn;

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (drill) {
      setTitle(e.target.value);
      drill.title = e.target.value;
    }
  };

  useEffect(() => {
    if (editing) {
      return;
    }
    const fromFirestore =
      drillDocumentData?.title != null &&
      String(drillDocumentData.title).trim() !== ""
        ? drillDocumentData.title
        : null;
    setTitle(fromFirestore ?? drill?.title ?? "");
  }, [
    drill,
    drillModifiedByCollaboration,
    drillDocumentData?.title,
    editing,
  ]);

  const undoChange = useCallback(() => {
    if (collaboration) {
      collaboration.undo();
    }
  }, [collaboration]);

  const redoChange = useCallback(() => {
    if (collaboration) {
      collaboration.redo();
    }
  }, [collaboration]);

  return (
    <div>
      <nav className="bg-gradient-to-r from-blue-600 to-blue-800 shadow-md">
        <div className="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-3">
          <a
            href="/my-designs"
            className="flex items-center space-x-3 rtl:space-x-reverse"
          >
            <img src="/logo-white.png" className="h-8" alt="DrillFlo Logo" />
            <span className="whitespace-nowrap text-xs font-medium px-2 py-0.5 rounded-full bg-white/20 text-white">
              BETA
            </span>
          </a>
          {/* {collaboratorPermission} */}
          <div className="flex items-center space-x-4">
            {editing ? (
              <input
                type="text"
                value={title}
                onChange={handleTitleChange}
                onBlur={() => setEditing(false)}
                onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
                className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-white/30 focus:outline-none"
                autoFocus
              />
            ) : (
              <button
                onClick={() => setEditing(true)}
                //   onClick={() =>
                //     collaboratorPermission !== "viewer" && setIsEditing(true)
                //   }
                className="text-white font-medium text-sm px-3 py-1.5 rounded-md hover:bg-white/10 transition-colors"
              >
                {title}
              </button>
            )}

            <div className="flex items-center space-x-2">
              <button
                onClick={undoChange}
                disabled={!canUndo || collaborationHistoryLoading}
                className={`p-1.5 rounded-md transition-colors ${canUndo && !collaborationHistoryLoading
                  ? "text-white hover:bg-white/10"
                  : "text-white/40"
                  }`}
              >
                {collaborationHistoryLoading ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 7v6h6"></path>
                    <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                  </svg>
                )}
              </button>
              <button
                onClick={redoChange}
                disabled={!canRedo || collaborationHistoryLoading}
                className={`p-1.5 rounded-md transition-colors ${canRedo && !collaborationHistoryLoading
                  ? "text-white hover:bg-white/10"
                  : "text-white/40"
                  }`}
              >
                {collaborationHistoryLoading ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 7v6h-6"></path>
                    <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"></path>
                  </svg>
                )}
              </button>
              <CollaborationHistory collaborationReturn={collaborationReturn} />
            </div>
            <CollaboratorButton
              documentId={drillDocumentData?.id}
              title={title}
              planVersion={userData?.plan}
              currentCollaborators={(otherCollaborators ?? []).map(
                (c) => `${c.firstName} ${c.lastName}`
              )}
              userData={auth?.currentUser?.uid}
            />
            <span className="text-xs text-white/70 px-2 py-1 rounded-md bg-white/10">
              {collaboration?.isConnected ? "Connected" : "Connecting..."}
            </span>
          </div>
        </div>
      </nav>
    </div>
  );
}
